import { supabase } from './authService'
import { HighlightItem, VideoItem } from '../types'

export interface DBHighlight {
  id: string
  home_team: string
  home_team_id: string | null
  away_team: string
  away_team_id: string | null
  league_id: string | null
  league_name: string | null
  league_country: string | null
  match_date: string
  match_id: string | null
  thumbnail: string | null
  embed_url: string | null
  embed_id: string
  user_id: string | null
  created_at: string | null
}

const HIGHLIGHTS_CACHE_KEY = 'xleven_global_highlights'
const CACHE_DURATION = 5 * 60 * 1000 // 5 minutes

interface CachedData {
  timestamp: number
  data: HighlightItem[]
}

export const fetchGlobalHighlights = async (forceRefresh = false, options: { leagueName?: string; limit?: number } = {}): Promise<HighlightItem[]> => {
  try {
    const cacheKey = options.leagueName ? `${HIGHLIGHTS_CACHE_KEY}_${options.leagueName}` : HIGHLIGHTS_CACHE_KEY;
    
    // Check cache first
    if (!forceRefresh) {
      const cached = localStorage.getItem(cacheKey)
      if (cached) {
        const parsed: CachedData = JSON.parse(cached)
        const now = Date.now()
        if (now - parsed.timestamp < CACHE_DURATION) {
          return parsed.data
        }
      }
    }

    let query = supabase
      .from('match_highlights')
      .select('*')
      .order('match_date', { ascending: false });

    if (options.leagueName) {
      query = query.ilike('league_name', `%${options.leagueName}%`);
    } else {
      const oneYearAgo = new Date()
      oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1)
      const dateStr = oneYearAgo.toISOString().split('T')[0]
      query = query.gte('match_date', dateStr);
    }

    if (options.limit) {
      query = query.limit(options.limit);
    } else if (!options.leagueName) {
      query = query.limit(50);
    }

    const { data, error } = await query;

    if (error) throw error

    const highlights: HighlightItem[] = (data as DBHighlight[]).map((item) => {
      return {
        title: `${item.home_team} vs ${item.away_team}`,
        competition: item.league_name || 'Other',
        league_id: item.league_id || undefined,
        home_team: item.home_team,
        home_team_id: item.home_team_id || undefined,
        away_team: item.away_team,
        away_team_id: item.away_team_id || undefined,
        matchviewUrl: '#',
        thumbnail: `https://img.youtube.com/vi/${item.embed_id}/maxresdefault.jpg`,
        date: item.match_date,
        embed_id: item.embed_id,
        match_id: item.match_id || undefined,
        videos: [{
          title: 'Highlight',
          embed: `https://www.youtube.com/embed/${item.embed_id}`,
        }],
      }
    })

    // Save to cache
    const cacheData: CachedData = {
      timestamp: Date.now(),
      data: highlights,
    }
    localStorage.setItem(cacheKey, JSON.stringify(cacheData))

    return highlights
  } catch (e) {
    console.error('Supabase Highlights Fetch Error:', e)
    return []
  }
}

/**
 * Fetches highlights specifically filtered by league ID or league name.
 * This is designed for the league detail view to bypass the 3-day window of global fetch.
 */
export const fetchHighlightsByLeague = async (
  leagueId: string,
  leagueName?: string,
  forceRefresh = false
): Promise<HighlightItem[]> => {
  try {
    const cacheKey = `highlights_league_${leagueId}_${leagueName || ''}`;
    
    if (!forceRefresh) {
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        const parsed: CachedData = JSON.parse(cached);
        if (Date.now() - parsed.timestamp < CACHE_DURATION) {
          return parsed.data;
        }
      }
    }

    let query = supabase
      .from('match_highlights')
      .select('*')
      .order('match_date', { ascending: false });

    // Try multiple filtering strategies
    if (!isNaN(parseInt(leagueId))) {
      // If it's a numeric ID (e.g., from football-data.org)
      query = query.eq('league_id', leagueId);
    } else if (leagueName) {
      // Fallback to name search
      query = query.ilike('league_name', `%${leagueName}%`);
    } else {
      // If only string ID provided, try it as league_name
      query = query.ilike('league_name', `%${leagueId}%`);
    }

    const { data, error } = await query;
    if (error) throw error;

    const highlights: HighlightItem[] = (data as DBHighlight[]).map((item) => ({
      title: `${item.home_team} vs ${item.away_team}`,
      competition: item.league_name || 'Other',
      league_id: item.league_id || undefined,
      home_team: item.home_team,
      home_team_id: item.home_team_id || undefined,
      away_team: item.away_team,
      away_team_id: item.away_team_id || undefined,
      matchviewUrl: '#',
      thumbnail: `https://img.youtube.com/vi/${item.embed_id}/maxresdefault.jpg`,
      date: item.match_date,
      embed_id: item.embed_id,
      match_id: item.match_id || undefined,
      videos: [{
        title: 'Highlight',
        embed: `https://www.youtube.com/embed/${item.embed_id}`,
      }],
    }));

    localStorage.setItem(cacheKey, JSON.stringify({
      timestamp: Date.now(),
      data: highlights
    }));

    return highlights;
  } catch (e) {
    console.error('Fetch highlights by league failed:', e);
    return [];
  }
};

export const groupHighlightsByLeague = (
  highlights: HighlightItem[],
): Record<string, HighlightItem[]> => {
  const grouped: Record<string, HighlightItem[]> = {}
  highlights.forEach((h) => {
    const league = h.competition || 'Other'
    if (!grouped[league]) grouped[league] = []
    grouped[league].push(h)
  })
  return grouped
}

// Admin Methods
export const fetchAllHighlights = async (): Promise<DBHighlight[]> => {
    const { data, error } = await supabase
        .from('match_highlights')
        .select('*')
        .order('match_date', { ascending: false });
    
    if (error) throw error;
    return data || [];
};

export const createHighlight = async (highlight: Omit<DBHighlight, 'id' | 'created_at' | 'thumbnail' | 'embed_url'>) => {
    const { data: { user } } = await supabase.auth.getUser();
    const insertData = {
        ...highlight,
        user_id: user?.id || null
    };

    const { data, error } = await supabase
        .from('match_highlights')
        .insert([insertData])
        .select();
    
    if (error) throw error;
    return data?.[0];
};

export const updateHighlight = async (id: string, highlight: Partial<DBHighlight>) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { thumbnail, embed_url, ...updateData } = highlight as any;
    const { data, error } = await supabase
        .from('match_highlights')
        .update(updateData)
        .eq('id', id)
        .select();
    
    if (error) throw error;
    return data?.[0];
};

export const deleteHighlight = async (id: string) => {
    const { error } = await supabase
        .from('match_highlights')
        .delete()
        .eq('id', id);
    
    if (error) throw error;
};

export const fetchMatchHighlightsFromSupabase = async (
  homeTeam: string,
  awayTeam: string,
  matchDate: string,
  gameId?: string,
): Promise<VideoItem[]> => {
  try {
    // If gameId is provided, ONLY match by match_id
    if (gameId) {
      const { data, error } = await supabase
        .from('match_highlights')
        .select('*')
        .eq('match_id', gameId)
        .order('match_date', { ascending: false })

      if (error) throw error
      if (!data || data.length === 0) return []

      return (data as DBHighlight[]).map((item) => ({
        id: item.embed_id,
        title: `${item.home_team} vs ${item.away_team} Highlights`,
        channel: item.league_name || 'XLEVEN Highlights',
        videoId: item.embed_id,
        description: `Official highlights for ${item.home_team} vs ${item.away_team} played on ${item.match_date}`,
        channelId: '',
      }))
    }

    // Fallback logic for when gameId is not available
    const date = new Date(matchDate)
    const startDate = new Date(date)
    startDate.setDate(date.getDate() - 3)
    const endDate = new Date(date)
    endDate.setDate(date.getDate() + 3)

    const startDateStr = startDate.toISOString().split('T')[0]
    const endDateStr = endDate.toISOString().split('T')[0]

    // 팀명을 더 유연하게 매칭하기 위해 첫 단어만 추출 (예: Aston Villa -> Aston)
    const homeSearch = homeTeam.split(' ')[0]
    const awaySearch = awayTeam.split(' ')[0]

    const { data, error } = await supabase
      .from('match_highlights')
      .select('*')
      .or(
        `home_team.ilike.%${homeSearch}%,away_team.ilike.%${homeSearch}%,home_team.ilike.%${awaySearch}%,away_team.ilike.%${awaySearch}%`,
      )
      .gte('match_date', startDateStr)
      .lte('match_date', endDateStr)
      .order('match_date', { ascending: false })

    if (error) throw error
    if (!data || data.length === 0) return []

    return (data as DBHighlight[]).map((item) => ({
      id: item.embed_id,
      title: `${item.home_team} vs ${item.away_team} Highlights`,
      channel: item.league_name || 'XLEVEN Highlights',
      videoId: item.embed_id,
      description: `Official highlights for ${item.home_team} vs ${item.away_team} played on ${item.match_date}`,
      channelId: '',
    }))
  } catch (e) {
    console.error('Supabase Match Highlights Fetch Error:', e)
    return []
  }
}
